import React, { useMemo } from 'react';
import { Line } from '@react-three/drei';
import { CanvasTexture, DoubleSide, RepeatWrapping, Shape, Texture, Vector2 } from 'three';

import { useAssistantState } from '../../assistant/assistantStore';
import { PREVIEW_COLORS, PreviewChange, PreviewShape } from '../../assistant/planPreviewRequest';

interface Point {
  readonly x: number;
  readonly y: number;
}

const HATCH_TEXTURE_SIZE = 64;
const textures = new Map<PreviewChange, Texture>();

/**
 * A tiling hatch, white on clear so the material's color tints it: diagonal for new and changed (opposite ways, so they
 * differ without color), crossed for removed. Nothing else in the app is hatched, so a preview can't pass for the model.
 */
function getHatchTexture(change: PreviewChange) {
  const existing = textures.get(change);
  if (existing != null) return existing;

  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = HATCH_TEXTURE_SIZE;
  const context = canvas.getContext('2d')!;
  context.strokeStyle = 'white';
  context.lineWidth = HATCH_TEXTURE_SIZE / 8;
  const stroke = (rising: boolean) => {
    // Three strokes so the diagonal joins up across tile edges.
    for (const offset of [-HATCH_TEXTURE_SIZE, 0, HATCH_TEXTURE_SIZE]) {
      context.beginPath();
      context.moveTo(offset, rising ? HATCH_TEXTURE_SIZE : 0);
      context.lineTo(offset + HATCH_TEXTURE_SIZE, rising ? 0 : HATCH_TEXTURE_SIZE);
      context.stroke();
    }
  };
  if (change !== 'change') stroke(true);
  if (change !== 'add') stroke(false);

  const texture = new CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = RepeatWrapping;
  textures.set(change, texture);
  return texture;
}

/** The outline to hatch: a closed shape as given, a line as a band of `width` around it. */
function getOutlines(shape: PreviewShape, width: number): Point[][] {
  if (shape.closed) return [[...shape.points]];
  return shape.points.slice(1).flatMap((end, index) => {
    const start = shape.points[index];
    const length = Math.hypot(end.x - start.x, end.y - start.y);
    if (length === 0) return [];
    const normal = { x: (-(end.y - start.y) / length) * (width / 2), y: ((end.x - start.x) / length) * (width / 2) };
    return [
      [
        { x: start.x + normal.x, y: start.y + normal.y },
        { x: end.x + normal.x, y: end.y + normal.y },
        { x: end.x - normal.x, y: end.y - normal.y },
        { x: start.x - normal.x, y: start.y - normal.y },
      ],
    ];
  });
}

function getLongestEdgeAngle(outline: readonly Point[]) {
  let longest = { length: 0, angle: 0 };
  outline.forEach((start, index) => {
    const end = outline[(index + 1) % outline.length];
    const length = Math.hypot(end.x - start.x, end.y - start.y);
    if (length > longest.length) longest = { length, angle: Math.atan2(end.y - start.y, end.x - start.x) };
  });
  return longest.angle;
}

function HatchedOutline({
  outline,
  change,
  hatchSpacing,
}: {
  outline: readonly Point[];
  change: PreviewChange;
  hatchSpacing: number;
}) {
  const shape = useMemo(() => new Shape(outline.map(({ x, y }) => new Vector2(x, y))), [outline]);
  // Shape geometry's UVs are its coordinates, so this spaces the hatch in inches whatever the shape's size. Turning it
  // with the shape's longest edge keeps the hatch across a member, never along it, however the member runs.
  const texture = useMemo(() => {
    const hatch = getHatchTexture(change).clone();
    hatch.repeat.set(1 / hatchSpacing, 1 / hatchSpacing);
    hatch.rotation = getLongestEdgeAngle(outline);
    hatch.needsUpdate = true;
    return hatch;
  }, [change, hatchSpacing, outline]);
  const border = useMemo(
    () => [...outline, outline[0]].map(({ x, y }): [number, number, number] => [x, y, 0]),
    [outline],
  );

  return (
    <>
      {/* Dims what's under the preview, so the hatch stands out on any color the model has there. */}
      <mesh renderOrder={9999}>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial
          color="black"
          transparent
          opacity={0.45}
          depthTest={false}
          depthWrite={false}
          side={DoubleSide}
        />
      </mesh>
      <mesh renderOrder={10000}>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial
          map={texture}
          color={PREVIEW_COLORS[change]}
          transparent
          opacity={0.9}
          depthTest={false}
          depthWrite={false}
          side={DoubleSide}
        />
      </mesh>
      <Line
        points={border}
        color={PREVIEW_COLORS[change]}
        lineWidth={2}
        transparent
        opacity={1}
        dashed={change === 'remove'}
        dashSize={hatchSpacing * 1.5}
        gapSize={hatchSpacing}
        depthTest={false}
        renderOrder={10001}
      />
    </>
  );
}

/**
 * Draws what the Assistant's waiting Plans will change, hatched and see-through over everything else: on the open truss
 * when `trussGuid` is given, in the layout's plan coordinates when it's `null`. `lineWidth` is how wide a line in the
 * preview, such as a new web or wall, is drawn, and `hatchSpacing` how far apart the hatch's lines are, both in inches.
 */
export function PlanPreviewVisuals({
  trussGuid,
  lineWidth,
  hatchSpacing,
}: {
  trussGuid: string | null;
  lineWidth: number;
  hatchSpacing: number;
}) {
  const chats = useAssistantState((state) => state.chats);
  const outlines = useMemo(
    () =>
      chats.flatMap((chat) => {
        const preview = chat.pendingPlan?.plan.preview;
        if (preview == null || preview.trussGuid !== trussGuid) return [];
        return preview.shapes.flatMap((shape) =>
          getOutlines(shape, lineWidth).map((outline) => ({ outline, change: shape.change })),
        );
      }),
    [chats, trussGuid, lineWidth],
  );

  return (
    <>
      {outlines.map(({ outline, change }, index) => (
        <HatchedOutline key={index} outline={outline} change={change} hatchSpacing={hatchSpacing} />
      ))}
    </>
  );
}
